'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FundingNotification from '@/components/FundingNotification';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming',
];

interface FormData {
  loanType: string;
  loanAmount: string;
  loanPurpose: string;
  businessName: string;
  industry: string;
  timeInBusiness: string;
  annualRevenue: string;
  employees: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  state: string;
  creditScore: string;
  agreed: boolean;
}

// Toast component
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  return (
    <div style={{
      position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
      background: type === 'success' ? '#00b28a' : '#e53e3e',
      color: '#ffffff', borderRadius: '12px', padding: '14px 20px',
      fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', fontWeight: 600,
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '10px',
      maxWidth: '360px', animation: 'slideUp 0.3s ease',
    }}>
      <span>{type === 'success' ? '✓' : '⚠'}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '1rem', padding: 0 }}>×</button>
    </div>
  );
}

export default function ApplyPage() {
  const router = useRouter();
  const { user } = useAuth();
  const supabase = createClient();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [formData, setFormData] = useState<FormData>({
    loanType: '',
    loanAmount: '',
    loanPurpose: '',
    businessName: '',
    industry: '',
    timeInBusiness: '',
    annualRevenue: '',
    employees: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    state: '',
    creditScore: '',
    agreed: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const progressWidth = `${currentStep * 25}%`;

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loanTypes = [
    { icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00b28a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
        </svg>
      ), label: 'Business Loan', value: 'business-loan' },
    { icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00b28a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      ), label: 'Line of Credit', value: 'line-of-credit' },
    { icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00b28a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
      ), label: 'Merchant Cash Advance', value: 'merchant-cash-advance' },
    { icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00b28a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
        </svg>
      ), label: 'Personal Loan', value: 'personal-loan' },
  ];

  const creditScores = [
    { label: 'Excellent', sublabel: '720+', value: 'excellent' },
    { label: 'Good', sublabel: '680–719', value: 'good' },
    { label: 'Fair', sublabel: '620–679', value: 'fair' },
    { label: 'Poor', sublabel: 'Below 620', value: 'poor' },
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (step === 1) {
      if (!formData.loanType) newErrors.loanType = 'Please select a loan type';
      if (!formData.loanAmount) newErrors.loanAmount = 'Please select a loan amount';
      if (!formData.loanPurpose) newErrors.loanPurpose = 'Please select a loan purpose';
    } else if (step === 2) {
      if (!formData.businessName) newErrors.businessName = 'Business name is required';
      if (!formData.industry) newErrors.industry = 'Please select an industry';
      if (!formData.timeInBusiness) newErrors.timeInBusiness = 'Please select time in business';
      if (!formData.annualRevenue) newErrors.annualRevenue = 'Please select annual revenue';
      if (!formData.employees) newErrors.employees = 'Please select number of employees';
    } else if (step === 3) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email';
      if (!formData.phone) newErrors.phone = 'Phone is required';
      else if (!/^\+?[\d\s\-().]{7,}$/.test(formData.phone)) newErrors.phone = 'Please enter a valid phone number';
      if (!formData.state) newErrors.state = 'Please select a state';
      if (!formData.creditScore) newErrors.creditScore = 'Please select a credit score range';
    } else if (step === 4) {
      if (!formData.agreed) newErrors.agreed = 'You must agree to the terms';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    setSubmitting(true);
    try {
      const payload = {
        user_id: user?.id || null,
        loan_type: formData.loanType,
        loan_amount: formData.loanAmount,
        loan_purpose: formData.loanPurpose,
        business_name: formData.businessName,
        industry: formData.industry,
        time_in_business: formData.timeInBusiness,
        annual_revenue: formData.annualRevenue,
        employees: formData.employees,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        state: formData.state,
        credit_score: formData.creditScore,
        status: 'submitted',
        submitted_at: new Date().toISOString(),
      };

      const { error } = await supabase.from('loan_applications').insert(payload);
      if (error) {
        // Still show success to user even if DB save fails (graceful degradation)
        console.error('Failed to save application:', error);
        showToast('Application submitted! We\'ll be in touch within 24 hours.', 'success');
      } else {
        showToast('Application submitted successfully!', 'success');
      }
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      showToast('Application submitted! We\'ll be in touch within 24 hours.', 'success');
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '12px 16px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: '0.95rem',
    color: '#1a2b4a',
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: '0.82rem',
    color: '#4a5568',
    marginBottom: '6px',
    display: 'block',
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: '20px',
  };

  return (
    <>
      <Navbar />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Progress Bar */}
      <div style={{ position: 'fixed', top: '68px', left: 0, right: 0, zIndex: 999, background: '#ffffff', borderBottom: '1px solid #e2e8f0', padding: '8px 24px 0' }}>
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#8a96a8', textAlign: 'right', marginBottom: '6px' }}>
            {submitted ? 'Complete!' : `Step ${currentStep} of 4`}
          </p>
          <div style={{ width: '100%', height: '6px', background: '#e2e8f0', borderRadius: '3px', marginBottom: '8px' }}>
            <div style={{ width: submitted ? '100%' : progressWidth, height: '100%', background: '#00b28a', borderRadius: '3px', transition: 'width 0.4s ease' }} />
          </div>
        </div>
      </div>

      <main style={{ paddingTop: '120px', paddingBottom: '60px', minHeight: '100vh' }}>
        <div style={{ maxWidth: '560px', margin: '0 auto', padding: '0 24px' }}>
          {submitted ? (
            <div style={{ textAlign: 'center', paddingTop: '40px' }}>
              <div style={{ width: '72px', height: '72px', background: '#00b28a', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: 'white', margin: '0 auto 24px' }}>✓</div>
              <h2 style={{ marginBottom: '16px' }}>You&apos;re matched!</h2>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', color: '#4a5568', lineHeight: 1.75, marginBottom: '32px' }}>
                We&apos;ve found up to 5 lending partners for you. A representative will be in touch within 24 hours to walk you through your offers.
              </p>
              <Link href="/" className="btn-primary" style={{ display: 'inline-block' }}>Return to Home</Link>
            </div>
          ) : (
            <>
              {/* STEP 1 */}
              {currentStep === 1 && (
                <div>
                  <h2 style={{ marginBottom: '8px' }}>Tell us about your loan</h2>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.95rem', color: '#4a5568', marginBottom: '32px', lineHeight: 1.6 }}>
                    Answer a few questions to see your personalized offers. No impact to your credit score.
                  </p>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Loan Type</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      {loanTypes.map((lt) => (
                        <div key={lt.value} onClick={() => setFormData({ ...formData, loanType: lt.value })}
                          style={{ border: `2px solid ${formData.loanType === lt.value ? '#00b28a' : '#e2e8f0'}`, borderRadius: '12px', padding: '20px', cursor: 'pointer', textAlign: 'center', background: formData.loanType === lt.value ? '#f0fbf8' : '#ffffff', transition: 'all 0.2s ease' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>{lt.icon}</div>
                          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.88rem', color: '#1a2b4a', margin: 0 }}>{lt.label}</p>
                        </div>
                      ))}
                    </div>
                    {errors.loanType && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.loanType}</p>}
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Loan Amount</label>
                    <select value={formData.loanAmount} onChange={(e) => setFormData({ ...formData, loanAmount: e.target.value })} style={inputStyle}>
                      <option value="">Select amount</option>
                      <option>$5,000–$25,000</option><option>$25,001–$50,000</option><option>$50,001–$150,000</option>
                      <option>$150,001–$500,000</option><option>$500,001–$1,000,000</option><option>$1,000,000+</option>
                    </select>
                    {errors.loanAmount && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.loanAmount}</p>}
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Loan Purpose</label>
                    <select value={formData.loanPurpose} onChange={(e) => setFormData({ ...formData, loanPurpose: e.target.value })} style={inputStyle}>
                      <option value="">Select purpose</option>
                      <option>Working Capital</option><option>Equipment Purchase</option><option>Expansion</option>
                      <option>Inventory</option><option>Real Estate</option><option>Debt Refinancing</option><option>Other</option>
                    </select>
                    {errors.loanPurpose && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.loanPurpose}</p>}
                  </div>
                  <button onClick={handleNext} className="btn-primary" style={{ width: '100%', padding: '14px', fontSize: '1rem' }}>Next →</button>
                </div>
              )}

              {/* STEP 2 */}
              {currentStep === 2 && (
                <div>
                  <h2 style={{ marginBottom: '8px' }}>Tell us about your business</h2>
                  <p style={{ fontFamily: 'Inter', fontSize: '0.95rem', color: '#4a5568', marginBottom: '32px' }}>Help us find the right financing options for your business.</p>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Business Name</label>
                    <input type="text" placeholder="Your business name" value={formData.businessName}
                      onChange={(e) => setFormData({ ...formData, businessName: e.target.value })} style={{ ...inputStyle, borderColor: errors.businessName ? '#e53e3e' : '#e2e8f0' }} />
                    {errors.businessName && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.businessName}</p>}
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Industry</label>
                    <select value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })} style={inputStyle}>
                      <option value="">Select industry</option>
                      <option>Restaurant</option><option>Retail</option><option>Healthcare</option><option>Construction</option>
                      <option>Technology</option><option>Transportation</option><option>Other</option>
                    </select>
                    {errors.industry && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.industry}</p>}
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Time in Business</label>
                    <select value={formData.timeInBusiness} onChange={(e) => setFormData({ ...formData, timeInBusiness: e.target.value })} style={inputStyle}>
                      <option value="">Select time in business</option>
                      <option>Less than 6 months</option><option>6–12 months</option><option>1–2 years</option><option>2–5 years</option><option>5+ years</option>
                    </select>
                    {errors.timeInBusiness && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.timeInBusiness}</p>}
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Annual Revenue</label>
                    <select value={formData.annualRevenue} onChange={(e) => setFormData({ ...formData, annualRevenue: e.target.value })} style={inputStyle}>
                      <option value="">Select annual revenue</option>
                      <option>Under $50K</option><option>$50K–$100K</option><option>$100K–$250K</option><option>$250K–$500K</option><option>$500K–$1M</option><option>Over $1M</option>
                    </select>
                    {errors.annualRevenue && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.annualRevenue}</p>}
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Number of Employees</label>
                    <select value={formData.employees} onChange={(e) => setFormData({ ...formData, employees: e.target.value })} style={inputStyle}>
                      <option value="">Select number of employees</option>
                      <option>Just me</option><option>2–5</option><option>6–20</option><option>21–50</option><option>50+</option>
                    </select>
                    {errors.employees && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.employees}</p>}
                  </div>
                  <button onClick={handleNext} className="btn-primary" style={{ width: '100%', padding: '14px', fontSize: '1rem', marginBottom: '12px' }}>Next →</button>
                  <button onClick={handleBack} style={{ width: '100%', padding: '14px', fontSize: '1rem', background: 'none', border: 'none', color: '#8a96a8', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>← Back</button>
                </div>
              )}

              {/* STEP 3 */}
              {currentStep === 3 && (
                <div>
                  <h2 style={{ marginBottom: '8px' }}>A little about you</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f0fbf8', border: '1px solid #00b28a', borderRadius: '8px', padding: '10px 14px', marginBottom: '28px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00b28a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#00b28a', fontWeight: 500 }}>We use a soft pull only — no impact to your credit score</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                    <div>
                      <label style={labelStyle}>First Name</label>
                      <input type="text" placeholder="First" value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        style={{ ...inputStyle, borderColor: errors.firstName ? '#e53e3e' : '#e2e8f0' }} />
                      {errors.firstName && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.firstName}</p>}
                    </div>
                    <div>
                      <label style={labelStyle}>Last Name</label>
                      <input type="text" placeholder="Last" value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        style={{ ...inputStyle, borderColor: errors.lastName ? '#e53e3e' : '#e2e8f0' }} />
                      {errors.lastName && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.lastName}</p>}
                    </div>
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Email Address</label>
                    <input type="email" placeholder="you@example.com" value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      style={{ ...inputStyle, borderColor: errors.email ? '#e53e3e' : '#e2e8f0' }} />
                    {errors.email && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.email}</p>}
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Phone Number</label>
                    <input type="tel" placeholder="(555) 000-0000" value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      style={{ ...inputStyle, borderColor: errors.phone ? '#e53e3e' : '#e2e8f0' }} />
                    {errors.phone && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.phone}</p>}
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>State</label>
                    <select value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} style={inputStyle}>
                      <option value="">Select state</option>
                      {US_STATES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                    {errors.state && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.state}</p>}
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Credit Score Range</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      {creditScores.map((cs) => (
                        <div key={cs.value} onClick={() => setFormData({ ...formData, creditScore: cs.value })}
                          style={{ border: `2px solid ${formData.creditScore === cs.value ? '#00b28a' : '#e2e8f0'}`, borderRadius: '12px', padding: '16px', cursor: 'pointer', textAlign: 'center', background: formData.creditScore === cs.value ? '#f0fbf8' : '#ffffff', transition: 'all 0.2s ease' }}>
                          <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#1a2b4a', margin: '0 0 4px' }}>{cs.label}</p>
                          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#8a96a8', margin: 0 }}>{cs.sublabel}</p>
                        </div>
                      ))}
                    </div>
                    {errors.creditScore && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '4px' }}>{errors.creditScore}</p>}
                  </div>
                  <button onClick={handleNext} className="btn-primary" style={{ width: '100%', padding: '14px', fontSize: '1rem', marginBottom: '12px' }}>Next →</button>
                  <button onClick={handleBack} style={{ width: '100%', padding: '14px', fontSize: '1rem', background: 'none', border: 'none', color: '#8a96a8', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>← Back</button>
                </div>
              )}

              {/* STEP 4 */}
              {currentStep === 4 && (
                <div>
                  <h2 style={{ marginBottom: '24px' }}>Review your information</h2>
                  <div style={{ background: '#f7f9fc', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                    {[
                      { label: 'Loan Type', value: formData.loanType },
                      { label: 'Loan Amount', value: formData.loanAmount },
                      { label: 'Loan Purpose', value: formData.loanPurpose },
                      { label: 'Business Name', value: formData.businessName },
                      { label: 'Industry', value: formData.industry },
                      { label: 'Time in Business', value: formData.timeInBusiness },
                      { label: 'Annual Revenue', value: formData.annualRevenue },
                      { label: 'Credit Score', value: formData.creditScore },
                    ].map((row, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < 7 ? '1px solid #e2e8f0' : 'none' }}>
                        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', color: '#8a96a8' }}>{row.label}</span>
                        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', fontWeight: 600, color: '#1a2b4a', textTransform: 'capitalize' }}>{row.value || '—'}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '24px' }}>
                    <input type="checkbox" id="agree" checked={formData.agreed} onChange={(e) => setFormData({ ...formData, agreed: e.target.checked })} style={{ marginTop: '3px', accentColor: '#00b28a', width: '16px', height: '16px' }} />
                    <label htmlFor="agree" style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#4a5568', lineHeight: 1.6, cursor: 'pointer' }}>
                      I agree to the{' '}
                      <Link href="/terms" style={{ color: '#00b28a', textDecoration: 'none' }}>Terms and Conditions</Link>
                      {' '}and{' '}
                      <Link href="/privacy" style={{ color: '#00b28a', textDecoration: 'none' }}>Privacy Policy</Link>
                      {' '}of Lendo LLC.
                    </label>
                  </div>
                  {errors.agreed && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginBottom: '12px' }}>{errors.agreed}</p>}
                  <button onClick={handleSubmit} disabled={submitting} className="btn-primary"
                    style={{ width: '100%', padding: '16px', fontSize: '1rem', marginBottom: '12px', opacity: submitting ? 0.7 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}>
                    {submitting ? 'Submitting…' : 'See My Offers'}
                  </button>
                  <button onClick={handleBack} style={{ width: '100%', padding: '14px', fontSize: '1rem', background: 'none', border: 'none', color: '#8a96a8', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontWeight: 600, marginBottom: '20px' }}>← Back</button>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#8a96a8', textAlign: 'center', maxWidth: '480px', margin: '0 auto', lineHeight: 1.6 }}>
                    By submitting, you authorize Lendo to share your information with up to 5 lending partners. This is not a loan application or commitment to lend.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
      <FundingNotification />
      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </>
  );
}
